import { useState } from 'react';

interface HeroProps {
  scrolled: boolean;
}

export default function Hero({ scrolled }: HeroProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    }
  };

  return (
    <section id="inicio" className="relative min-h-screen overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a0b2e] via-[#2d1b4e] to-[#0f0520]">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#ff00ff] rounded-full mix-blend-screen filter blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-[#00ffff] rounded-full mix-blend-screen filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-[#ff00aa] rounded-full mix-blend-screen filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#1a0b2e]/95 backdrop-blur-lg shadow-lg shadow-[#ff00ff]/20' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('inicio')}>
              <img
                src="https://static.readdy.ai/image/36e200659a4cc97e8ffc8ac4660b08aa/99dc07e31ad03e137a63ec7bb4222661.png"
                alt="Logo Ideas"
                className="w-12 h-12 rounded-full border-2 border-[#00ffff] object-cover shadow-lg shadow-[#00ffff]/50"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-[#ff00ff] via-[#00ffff] to-[#ff00aa] bg-clip-text text-transparent">Logo Ideas</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-10">
              <button onClick={() => scrollToSection('inicio')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Inicio
              </button>
              <button onClick={() => scrollToSection('servicios')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Servicios
              </button>
              <button onClick={() => scrollToSection('portafolio')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Portafolio
              </button>
              <button onClick={() => scrollToSection('testimonios')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Testimonios
              </button>
              <button onClick={() => scrollToSection('contacto')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Contacto
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden text-2xl cursor-pointer text-[#00ffff]"
            >
              <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="md:hidden mt-4 pb-4 flex flex-col gap-4 bg-[#1a0b2e]/90 backdrop-blur-lg rounded-2xl p-4">
              <button onClick={() => scrollToSection('inicio')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Inicio
              </button>
              <button onClick={() => scrollToSection('servicios')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Servicios
              </button>
              <button onClick={() => scrollToSection('portafolio')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Portafolio
              </button>
              <button onClick={() => scrollToSection('testimonios')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Testimonios
              </button>
              <button onClick={() => scrollToSection('contacto')} className="text-sm font-semibold text-white hover:text-[#00ffff] transition-colors uppercase cursor-pointer whitespace-nowrap">
                Contacto
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Content */}
      <div className="pt-24 min-h-screen flex items-center relative z-10">
        <div className="w-full max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            {/* Left Content */}
            <div className="md:col-span-3 space-y-8">
              <div className="inline-block bg-gradient-to-r from-[#ff00ff]/20 to-[#00ffff]/20 backdrop-blur-sm border border-[#00ffff]/30 px-6 py-2 rounded-full">
                <span className="text-xs font-bold text-[#00ffff] uppercase tracking-wide">
                  Agencia Creativa Profesional
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                <span className="block text-white">Diseños que convierten</span>
                <span className="block text-white">ideas en marcas</span>
                <span className="block bg-gradient-to-r from-[#ff00ff] via-[#00ffff] to-[#ff00aa] bg-clip-text text-transparent italic">memorables</span>
              </h1>

              <p className="text-xl text-[#c0c0ff] leading-relaxed max-w-2xl">
                Logotipos, banners, flyers y diseños para redes sociales que impulsan tu negocio
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="https://wa.me/527131055034"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#ff00ff] to-[#ff00aa] text-white px-10 py-4 rounded-full font-semibold hover:scale-105 transition-transform cursor-pointer whitespace-nowrap shadow-lg shadow-[#ff00ff]/50"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  CONTÁCTANOS POR WHATSAPP
                </a>
                <button
                  onClick={() => scrollToSection('portafolio')}
                  className="inline-flex items-center justify-center border-2 border-[#00ffff] bg-transparent text-[#00ffff] px-10 py-4 rounded-full font-semibold hover:bg-[#00ffff] hover:text-[#1a0b2e] transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-[#00ffff]/30"
                >
                  VER PORTAFOLIO
                </button>
              </div>

              <div className="pt-2">
                <a
                  href="https://www.facebook.com/share/1AefDud4bR/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#00ffff] to-[#0099ff] text-white px-10 py-4 rounded-full font-semibold hover:scale-105 transition-transform cursor-pointer whitespace-nowrap shadow-lg shadow-[#00ffff]/50"
                >
                  <i className="ri-facebook-fill text-xl"></i>
                  SÍGUENOS EN FACEBOOK
                </a>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="md:col-span-2 relative">
              <div className="relative bg-gradient-to-br from-[#ff00ff]/20 to-[#00ffff]/20 backdrop-blur-sm border border-[#00ffff]/30 rounded-3xl p-8 md:p-12 min-h-[500px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-[#ff00ff]/10 via-transparent to-[#00ffff]/10"></div>
                <img
                  src="https://public.readdy.ai/ai/img_res/a954b80fe42c7cd190d0192b52112901.jpg"
                  alt="Diseños Profesionales"
                  className="relative z-10 w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
